// Lib
import React, { useCallback, useRef } from 'react';
import { Popover, UtilityButton } from '@milanote/roma';
import { EditorToolbar } from '@milanote/roma/application';

// Utils
import { stopDefaultAndPropagation } from '../../../../utils/domUtil';

// Components
import Icon from '../../../../components/icons/Icon';

// Hooks
import useTouchBlockedPointerEvent from './useTouchBlockedPointerEvent';
import { useTranslator } from '../../../../components/i18n/useTranslator';

// Constants
import { STROKE_SIZES } from '../drawingEditorConstants';

type StrokeSize = (typeof STROKE_SIZES)[keyof typeof STROKE_SIZES];

type DrawingEditorStrokeSizeToolV2Props = {
    disabled: boolean;
    isPenMode: boolean;
    keepPopupOpen: boolean;
    strokeSize: StrokeSize;
    setStrokeSize: (strokeSize: StrokeSize) => void;
    strokePopupOpen: boolean;
    setStrokePopupOpen: (isOpen: boolean) => void;
};

const STROKE_SIZE_OPTIONS = [
    STROKE_SIZES.SMALL,
    STROKE_SIZES.MEDIUM,
    STROKE_SIZES.LARGE,
    STROKE_SIZES.EXTRA_LARGE,
] as const;

const getIconName = (strokeSize: StrokeSize) => {
    switch (strokeSize) {
        case STROKE_SIZES.SMALL:
            return 'drawing-editor-stroke-1-v2';
        case STROKE_SIZES.LARGE:
            return 'drawing-editor-stroke-3-v2';
        case STROKE_SIZES.EXTRA_LARGE:
            return 'drawing-editor-stroke-4-v2';
        case STROKE_SIZES.MEDIUM:
        default:
            return 'drawing-editor-stroke-2-v2';
    }
};

const StrokeSizeIcon: React.FC<{ strokeSize: StrokeSize }> = ({ strokeSize }) => (
    <Icon name={getIconName(strokeSize)} />
);

/**
 * v2 (Roma) stroke-size tool. Uses the Roma popover/list pattern from EditorToolbar examples.
 */
const DrawingEditorStrokeSizeToolV2: React.FC<DrawingEditorStrokeSizeToolV2Props> = ({
    disabled,
    isPenMode,
    keepPopupOpen,
    strokeSize,
    setStrokeSize,
    strokePopupOpen,
    setStrokePopupOpen,
}) => {
    const translate = useTranslator('popup', 'drawing');
    const triggerRef = useRef<HTMLButtonElement>(null);
    const closePopup = () => setStrokePopupOpen(false);
    const togglePopup = useCallback(() => setStrokePopupOpen(!strokePopupOpen), [setStrokePopupOpen, strokePopupOpen]);
    const onButtonPointerUp = useTouchBlockedPointerEvent(isPenMode, togglePopup);

    const setStrokeSizeAndClose = (_event: React.PointerEvent<HTMLButtonElement>, size: StrokeSize) => {
        setStrokeSize(size);
        !keepPopupOpen && closePopup();
    };

    return (
        <>
            <UtilityButton
                ref={triggerRef}
                isDisabled={disabled}
                onPointerUp={!disabled ? onButtonPointerUp : undefined}
                onDoubleClick={stopDefaultAndPropagation}
                tooltip={{ title: translate('tools.lineWeight') }}
            >
                <StrokeSizeIcon strokeSize={strokeSize} />
            </UtilityButton>

            <Popover
                triggerRef={triggerRef}
                isOpen={strokePopupOpen}
                onOpenChange={setStrokePopupOpen}
                placement="top"
                popoverClassName="p-md"
            >
                <EditorToolbar.List aria-label={translate('tools.lineWeightTools')}>
                    {STROKE_SIZE_OPTIONS.map((strokeSizeOption) => (
                        <UtilityButton
                            key={strokeSizeOption}
                            isSelected={strokeSizeOption === strokeSize}
                            onPointerUp={(event) => setStrokeSizeAndClose(event, strokeSizeOption)}
                            onDoubleClick={stopDefaultAndPropagation}
                        >
                            <StrokeSizeIcon strokeSize={strokeSizeOption} />
                        </UtilityButton>
                    ))}
                </EditorToolbar.List>
            </Popover>
        </>
    );
};

export default DrawingEditorStrokeSizeToolV2;
